import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import MonitoringFeaturesTable from './MonitoringFeaturesTable';
import { 
  Clock, Camera, Bell, Shield, Infinity, CreditCard, 
  Zap, CheckCircle2, Trash2, ArrowRight, Star, MapPinned,
  Phone
} from 'lucide-react';
import Navbar from '../components/Navbar';
import './Landing.css';

function Landing() {
  const { i18n } = useTranslation();
  const [activeFaq, setActiveFaq] = useState(null);

  useEffect(() => {
    // localStorage'dan dili oku ve i18n'e uygula
    const savedLanguage = localStorage.getItem('rux_language');
    if (savedLanguage && savedLanguage !== i18n.language) {
      i18n.changeLanguage(savedLanguage);
    }
  }, []);

  const lang = i18n.language === 'tr' ? 'tr' : 'en';


  // FAQ Data
  const faqData = lang === 'tr' ? [
    {
      q: "Kullanıcılar siteye nasıl kayıt olur ?",
      a: "Kullanıcılar sisteme kayıt olmayı istediklerinde mail adresleri, ad soyad ve belirledikleri şifreleri ile her zaman kayıt olabilirler."
    },
    {
      q: "Kullanıcılara bildirimler nasıl gidiyor ?",
      a: "Kullanıcılara bildirimler e-posta olarak iletilmektedir."
    },
    {
      q: "Web sitelerinin hata takibi ne kadar sürede yapılıyor ?",
      a: "Rux Engine sistemi kullanıcıların web sitelerini dakikada (60 saniyede) bir defa kontrol eder."
    },
    {
      q: "Web sitelerinin ekran görüntüsü ne zaman alınıyor ?",
      a: "Hata durumunda ekran görüntüsü otomatik alınır ve 7 gün saklanır."
    },
    {
      q: "7 günlük ücretsiz deneme nasıl çalışır ?",
      a: "Kayıt olduktan sonra 7 gün ücretsiz kullanım hakkınız oluyor. Kredi kartı bilgisi gerekmez."
    },
    {
      q: "İptal ve iade nasıl oluyor ?",
      a: "7 günlük deneme süresi sonunda web sitenizi silebilir veya hizmet süresini uzatabilirsiniz. Deneme süresi bitiminde otomatik olarak herhangi bir ödeme tahsil edilmez. Sistem üzerinden eklenen alan adları (domainler) için yapılan ödemeler kesin olup, iptal ve iade edilmez."
    },
    {
      q: "Kredi kartı bilgilerim kayıt ediliyor mu ?",
      a: "Hayır, kredi kartı bilgileriniz sistemimizde SAKLANMAMAKTADIR. Her ödeme için yeniden girmeniz gerekir."
    },
    {
      q: "En fazla kaç site ekleyebilirim ?",
      a: "Herhangi bir web site limiti yoktur. İstediğiniz kadar web sitenizi ekleyebilirsiniz."
    },
    {
      q: "Ekran görüntüleri ne zaman silinir ?",
      a: "Alınmış olan ekran görüntüleri 7 gün sonra otomatik olarak silinir."
    }
  ] : [
    {
      q: "How do users register ?",
      a: "Users can register with email, full name and password at any time."
    },
    {
      q: "How are notifications sent ?",
      a: "Notifications are sent via email to users."
    },
    {
      q: "How often are websites checked ?",
      a: "RUX Engine checks your websites once per minute (every 60 seconds)."
    },
    {
      q: "When are screenshots taken ?",
      a: "Screenshots are automatically taken when errors occur and stored for 7 days."
    },
    {
      q: "How does the 7-day free trial work ?",
      a: "After registration, you get 7 days free usage. No credit card required."
    },
    {
      q: "How does cancellation and refund work ?",
      a: "At the end of the 7-day trial period, you may delete your website or extend the service period. No automatic payment will be charged after the trial period ends. Payments made for domains added to the system are final and are non-refundable and non-cancellable."
    },
    {
      q: "Are credit card details stored ?",
      a: "No, credit card information is NOT stored. You must enter it again for each payment."
    },
    {
      q: "How many sites can I add ?",
      a: "There is no website limit. You can add as many websites as you want."
    },
    {
      q: "When are screenshots deleted ?",
      a: "Screenshots are automatically deleted after 7 days."
    }
  ];

  const toggleFaq = (index) => {
    setActiveFaq(activeFaq === index ? null : index);
  };

  return (
    <div className="landing-page">
      {/* Header */}
      <header className="landing_header">
        <div className="container">
          <Navbar />
        </div>
      </header>

      {/* Hero */}
      <section className="hero">
        <div className="container">
          <h1>
            {lang === 'tr' ? (
              <>Web Sitelerinizi <span className="gradient-text">Gerçek Zamanlı</span> İzleyin</>
            ) : (
              <>Monitor Your Websites in <em className="gradient-text">Real-Time</em></>
            )}
          </h1>
          <p>
            {lang === 'tr' 
              ? 'Dakikada bir kontrol, anında bildirim, otomatik ekran görüntüsü — hepsi tek yerde.' 
              : 'Check every minute, instant notifications, automatic screenshots — all in one place.'}
          </p>
          <div className="hero-buttons">
            <a href="/register" className="btn btn-primary">
              {lang === 'tr' ? 'Ücretsiz Başla' : 'Start Free'}
              <ArrowRight size={20} style={{ marginLeft: '8px', display: 'inline' }} />
            </a>
            <span className="live-demo">
              <span className="live-dot"></span>
              {lang === 'tr' ? 'Web Site Kontrol' : 'Website Check'}
            </span>
          </div>
          <div className="heroImage">
            <img 
              className="hero-image" 
              src="/images/rux-image-1-scaled.webp" 
              alt="RUX Engine Dashboard" 
              loading="eager"
            />
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="stats">
        <div className="container">
          <div className="stats-grid">
            <div className="stat-item">
              <h3>99.99%</h3>
              <p>{lang === 'tr' ? 'UPTIME GARANTİSİ' : 'UPTIME GUARANTEE'}</p>
            </div>
            <div className="stat-item">
              <h3>1 {lang === 'tr' ? 'dk' : 'min'}</h3>
              <p>{lang === 'tr' ? 'KONTROL SIKLIĞI' : 'CHECK FREQUENCY'}</p>
            </div>
            <div className="stat-item">
              <h3>7 {lang === 'tr' ? 'Gün' : 'Days'}</h3>
              <p>{lang === 'tr' ? 'ÜCRETSİZ DENEME' : 'FREE TRIAL'}</p>
            </div>
            <div className="stat-item">
              <h3>{lang === 'tr' ? 'Anında' : 'Instant'}</h3>
              <p>{lang === 'tr' ? 'EKRAN GÖRÜNTÜSÜ' : 'SCREENSHOT'}</p>
            </div>
          </div>
        </div>
      </section>

      <section className="ruxImage">
        <div className="container">
          <img src="/images/rux-image-2-scaled.webp" alt="RUX Engine Dashboard" className="img" />
        </div>
      </section>

      {/* Features */}
      <section id="features" className="core-features">
        <div className="container">
          <h2 className="section-title">
            {lang === 'tr' ? 'RUX Engine\'in Gücü' : 'The Power of RUX Engine'}
          </h2>
          <div className="features-grid">
            <div className="feature-card">
              <div className="feature-icon">
                <Clock size={36} strokeWidth={2} color="#6366f1" />
              </div>
              <h3>{lang === 'tr' ? 'Gerçek Zamanlı İzleme' : 'Real-Time Monitoring'}</h3>
              <p>
                {lang === 'tr' 
                  ? 'Web siteleriniz dakikada bir kontrol edilir. HTTP hataları, SSL hataları, zaman aşımı anında tespit edilir.' 
                  : 'Your websites are checked every minute. HTTP errors, SSL errors, and timeouts are detected instantly.'}
              </p>
            </div>

            <div className="feature-card">
              <div className="feature-icon">
                <Camera size={36} strokeWidth={2} color="#6366f1" />
              </div>
              <h3>{lang === 'tr' ? 'Akıllı Ekran Görüntüsü' : 'Smart Screenshots'}</h3>
              <p>
                {lang === 'tr' 
                  ? 'Sadece yeni hata durumlarında ekran görüntüsü alınır.' 
                  : 'Screenshots are only taken for new error situations.'}
              </p>
            </div>

            <div className="feature-card">
              <div className="feature-icon">
                <Bell size={36} strokeWidth={2} color="#6366f1" />
              </div>
              <h3>{lang === 'tr' ? 'Anlık Bildirimler' : 'Instant Notifications'}</h3>
              <p>
                {lang === 'tr' 
                  ? 'E-posta üzerinden anında uyarı alın.' 
                  : 'Get instant alerts via email.'}
              </p>
            </div>

            <div className="feature-card">
              <div className="feature-icon">
                <Shield size={36} strokeWidth={2} color="#6366f1" />
              </div>
              <h3>{lang === 'tr' ? 'Gizlilik & Güvenlik' : 'Privacy & Security'}</h3>
              <p>
                {lang === 'tr' 
                  ? 'Hiçbir kod eklenmez. Verileriniz 7 gün sonra otomatik silinir. KVKK ve GDPR uyumlu.' 
                  : 'No code required. Data auto-deleted after 7 days. GDPR compliant.'}
              </p>
            </div>

            <div className="feature-card">
              <div className="feature-icon">
                <Infinity size={36} strokeWidth={2} color="#6366f1" />
              </div>
              <h3>{lang === 'tr' ? 'Sınırsız Site' : 'Unlimited Sites'}</h3>
              <p>
                {lang === 'tr' 
                  ? 'İstediğiniz kadar site izleyin.' 
                  : 'Monitor as many sites as you want.'}
              </p>
            </div>

            <div className="feature-card">
              <div className="feature-icon">
                <CreditCard size={36} strokeWidth={2} color="#6366f1" />
              </div>
              <h3>{lang === 'tr' ? 'Güvenli Ödeme' : 'Secure Payment'}</h3>
              <p>
                {lang === 'tr' 
                  ? 'Kredi kartı bilgileriniz saklanmaz.' 
                  : 'Credit card info is never stored.'}
              </p>
            </div>

            <div className="feature-card">
              <div className="feature-icon">
                <Zap size={36} strokeWidth={2} color="#6366f1" />
              </div>
              <h3>{lang === 'tr' ? 'Sıfır Entegrasyon' : 'Zero Integration'}</h3>
              <p>
                {lang === 'tr' 
                  ? 'Web sitenize kod eklemenize gerek yok.' 
                  : 'No code installation required.'}
              </p>
            </div>

            <div className="feature-card">
              <div className="feature-icon">
                <CheckCircle2 size={36} strokeWidth={2} color="#6366f1" />
              </div>
              <h3>{lang === 'tr' ? 'Güvenli Sahiplik Kontrolü' : 'Secure Ownership Verification'}</h3>
              <p>
                {lang === 'tr' 
                  ? 'Siteleriniz başkaları tarafından izlenemez.' 
                  : 'Your sites cannot be monitored by others.'}
              </p>
            </div>

            <div className="feature-card">
              <div className="feature-icon">
                <Trash2 size={36} strokeWidth={2} color="#6366f1" />
              </div>
              <h3>{lang === 'tr' ? 'Otomatik Gizlilik' : 'Auto Privacy'}</h3>
              <p>
                {lang === 'tr' 
                  ? 'Ekran görüntüleri 7 gün sonra otomatik silinir.' 
                  : 'Screenshots auto-deleted after 7 days.'}
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="props">
        <MonitoringFeaturesTable lang={lang} />
      </section>

      {/* Pricing */}
      <section id="pricing" class="pricing-flex">
        <div class="container">

          <div class="pricing-flex-header">
            <div class="pricing-flex-badge">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z"/></svg>
              {lang === 'tr' ? 'Abonelik Yok' : 'No Subscription'}
            </div>

            <h2 class="pricing-flex-title">
              {lang === 'tr' ? (<>Basit Fiyat, <em>Senin Kuralların</em></>) : (<>Simple Price, <em>Your Rules</em></>)}
            </h2>

            <p class="pricing-flex-subtitle">
              {lang === 'tr' 
                ? 'Aylık sabit fiyat. İstediğin süre kadar al, istediğin zaman bırak. Taahhüt yok, sürpriz yok.'
                : 'Flat monthly rate. Pick your own duration, leave whenever you want. No commitment, no surprises.'}
            </p>
          </div>

          <div class="pricing-flex-main">

            <div class="pricing-flex-left">
              <div class="price-label">
                {lang === 'tr' ? 'Aylık Birim Fiyat' : 'Monthly Unit Price'}
              </div>
              <div class="price-amount">
                <span class="price-currency">$</span>
                <span class="price-value">5</span>
                <span class="price-decimal"></span>
              </div>
              <div class="price-period">
                {lang === 'tr' ? '/ ay, url başına' : '/ month, per url'}
              </div>
              <div class="no-subscription-tag">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><path d="m9 12 2 2 4-4"/></svg>
                {lang === 'tr' ? 'Otomatik yenileme yok' : 'No auto-renewal'}
              </div>
            </div>

            <div class="pricing-flex-right">
              <h3 class="flex-tagline">
                {lang === 'tr' ? "It's up to you." : "It's up to you."}
              </h3>
              <ul class="flex-features">
                <li>
                  <span class="feat-icon green">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12h14"/><path d="M12 5v14"/></svg>
                  </span>
                  <span>
                    {lang === 'tr' 
                      ? <><strong>1 ile 12 ay arası</strong> istediğin süreyi kendin belirle</>
                      : <><strong>Choose 1 to 12 months</strong> — set your own duration</>}
                  </span>
                </li>
                <li>
                  <span class="feat-icon orange">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z"/><path d="M12 6v6l4 2"/></svg>
                  </span>
                  <span>
                    {lang === 'tr'
                      ? <><strong>Süre bitince otomatik durur</strong> — gizli ücret asla yok</>
                      : <><strong>Stops when time is up</strong> — never any hidden charges</>}
                  </span>
                </li>
                <li>
                  <span class="feat-icon purple">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><path d="m9 11 3 3L22 4"/></svg>
                  </span>
                  <span>
                    {lang === 'tr'
                      ? <><strong>Tüm özellikler dahil</strong> — monitoring, bildirimler, ekran görüntüleri</>
                      : <><strong>All features included</strong> — monitoring, alerts, screenshots</>}
                  </span>
                </li>
              </ul>
              <a href="/register" class="pricing-cta-btn">
                <span>{lang === 'tr' ? '7 Gün Ücretsiz Dene' : 'Try 7 Days Free'}</span>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>
              </a>
            </div>
          </div>

          <div class="pricing-flex-highlights">
            <div class="highlight-item">
              <div class="highlight-icon hi-orange">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="2" y="5" width="20" height="14" rx="2"/><line x1="2" y1="10" x2="22" y2="10"/></svg>
              </div>
              <h4>
                {lang === 'tr' ? 'Kredi Kartı Saklanmaz' : 'No Card Stored'}
              </h4>
              <p>
                {lang === 'tr' ? 'Her ödeme tek seferlik. Kartınız sistemde tutulmaz.' : 'Every payment is one-time. Your card is never stored.'}
              </p>
            </div>
            <div class="highlight-item">
              <div class="highlight-icon hi-green">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
              </div>
              <h4>
                {lang === 'tr' ? 'Şeffaf ve Güvenli' : 'Transparent & Secure'}
              </h4>
              <p>
                {lang === 'tr' ? 'Ne ödeyeceğini baştan bilirsin.' : 'You know exactly what you pay.'}
              </p>
            </div>
            <div class="highlight-item">
              <div class="highlight-icon hi-purple">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
              </div>
              <h4>
                {lang === 'tr' ? '7 Gün Ücretsiz Deneme' : '7-Day Free Trial'}
              </h4>
              <p>
                {lang === 'tr' ? 'Kart bilgisi gerekmez. Beğenmezsen hiçbir şey ödemezsin.' : 'No card required. If you don\'t like it, you pay nothing.'}
              </p>
            </div>
          </div>

        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="faq">
        <div className="container">
          <h2 className="section-title">
            {lang === 'tr' ? 'Sık Sorulan Sorular' : 'Frequently Asked Questions'}
          </h2>
          <div className="faq-list">
            {faqData.map((item, index) => (
              <div 
                key={index} 
                className={`faq-item ${activeFaq === index ? 'active' : ''}`}
              >
                <div className="faq-question" onClick={() => toggleFaq(index)}>
                  {item.q}
                </div>
                <div className="faq-answer">{item.a}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="cta">
        <div className="container">
          <h2>
            {lang === 'tr' ? 'Hemen Bugün Ücretsiz Deneyin' : 'Try Free Today'}
          </h2>
          <p>
            {lang === 'tr' 
              ? 'İlk 7 gün boyunca sınırsız şekilde ücretsiz kullanın. Kredi kartı gerekmez.*' 
              : 'Use unlimited for the first 7 days for free. No credit card required.*'}
          </p>
          <a href="/register" className="btn btn-primary">
            {lang === 'tr' ? 'Hesap Oluştur' : 'Create Account'}
            <ArrowRight size={20} style={{ marginLeft: '8px', display: 'inline' }} />
          </a>
        </div>
      </section>

      {/* Footer */}
      <footer>
        <div className="container">
          <div className="footer-content">
            <div className="footer-col">
              <img src="/images/ruxLogo-96.png" alt="RUX Engine" className="footer-logo" />
              <div className="logo">RUX Engine</div>
              <p>
                {lang === 'tr' 
                  ? 'Web sitelerinizin sağlığını koruyan akıllı izleme platformu.' 
                  : 'Smart monitoring platform protecting your websites\' health.'}
              </p>
            </div>
            <div className="footer-col">
              <h4>{lang === 'tr' ? 'Şirket' : 'Company'}</h4>
              <a href="/privacy">{lang === 'tr' ? 'Gizlilik' : 'Privacy'}</a>
              <a href="/terms">{lang === 'tr' ? 'Şartlar' : 'Terms'}</a>
              <MapPinned size={20} style={{ display: 'inline' }} />  Kazım Karabekir Mah. 4213 sokak No:37:2 Adana - Yüreğir, Türkiye
              <div style={{marginTop:'12px'}}>
                <a style={{color:'#a0a0a0', fontSize:'12px'}} href="tel:+905444819168"> <Phone size={16} style={{ display: 'inline' }} /> Phone:  +905444819168</a>
              </div>
            </div>
            <div className="footer-col">
              <h4>{lang === 'tr' ? 'Destek' : 'Support'}</h4>
              <a href="#features">{lang === 'tr' ? 'Özellikler' : 'Features'}</a>
              <a href="#pricing">{lang === 'tr' ? 'Fiyatlandırma' : 'Pricing'}</a>
              <a href="#faq">{lang === 'tr' ? 'SSS' : 'FAQs'}</a>
            </div>
          </div>
          <div className="copyright">
            © 2026 RUX Engine. {lang === 'tr' ? 'Tüm hakları saklıdır.' : 'All rights reserved.'}
          </div>
        </div>
      </footer>
    </div>
  );
}

export default Landing;
